<template>
  <div>
    <div v-if="siblings" class="mb-7 md:hidden">
      <span class="text-xs text-gray-300">
        {{ $t('listing.sibblings') }}
      </span>
      <div class="mt-2 line-clamp-2 flex flex-wrap gap-2 overflow-hidden">
        <NuxtLink
          v-for="tag in siblings"
          :key="tag.id"
          :to="getCategoryRoute(tag)"
          class="group flex items-center justify-between gap-6 rounded-full border border-gray-100 px-4 py-2 text-xs text-gray-400 outline-none transition-colors hover:border-black hover:text-black focus:outline-none"
          :class="{
            'cursor-default bg-gray-100 hover:border-gray-100 hover:text-gray-400':
              tag.id === category.id,
          }"
        >
          {{ getTranslatedProperty(tag, 'name') }}
        </NuxtLink>
      </div>
    </div>
    <div class="flex gap-8">
      <div class="w-1/5 max-md:hidden">
        <slot name="title">
          <h3 class="px-4 pb-4 text-sm font-bold text-gray-800">
            {{ useUseGetCategoryName(props.category) }}
          </h3>
        </slot>
        <ul>
          <ShopListingNavigationItem
            v-for="item in categories"
            :key="item.id"
            :category="item"
            :active="category"
          />
        </ul>
      </div>
      <div class="flex flex-col lg:w-4/5 lg:flex-col">
        <div class="flex flex-wrap items-start justify-between pb-4">
          <div class="grow">
            <ShopListingFilters
              v-if="enableFilters"
              v-model="criteria"
              :available-filters="availableFilters"
            />
          </div>
          <div class="flex items-center gap-8">
            <InputText
              v-if="!enableFilters"
              v-model="search"
              :label="
                $t('general.searchIn', {
                  name: getTranslatedProperty(category, 'name'),
                })
              "
              class="max-md:hidden"
              icon="search"
            />
            <ShopListingSorting
              :options="sortingOrders"
              :selected="currentSortingOrder"
              align-options="right"
              class="shrink-0"
              @select="changeCurrentSortingOrder"
            />
          </div>
        </div>

        <span class="mb-2 text-xs text-gray-300 max-md:hidden">
          {{ $t('listing.numberOfProducts', { count: total }) }}
        </span>
        <div
          v-if="loadingProducts"
          class="flex size-full items-center justify-center"
        >
          <IconLoading class="size-8" />
        </div>
        <template v-else>
          <div
            v-if="products.length"
            class="grid grid-cols-2 gap-4 lg:grid-cols-4"
          >
            <ShopProductCard
              v-for="product in products"
              :key="product.id"
              :product="product"
            />
          </div>
          <div v-else class="flex grow items-center justify-center">
            <span class="text-xl font-medium text-gray-400">
              {{ $t('listing.noProducts') }}
            </span>
          </div>
        </template>

        <div
          v-if="totalPages > 1 && page < totalPages"
          class="mt-8 flex justify-center"
        >
          <AppButton :loading="loadingMore" @click="loadMore">
            {{ $t('listing.loadMore') }}
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'

const props = withDefaults(
  defineProps<{
    category: Schemas['Category']
    categoryTree?: Schemas['Category'][]
    enableFilters?: boolean
  }>(),
  {
    enableFilters: false,
  }
)
const route = useRoute()

const { getCategoryRoute } = useShopRouter()

const {
  search: searchProducts,
  getElements: products,
  getAvailableFilters: availableFilters,
  loadMore: coreLoadMore,
  loadingMore,
  loading: loadingProducts,
  getTotal: total,
  getCurrentPage: page,
  getTotalPagesCount: totalPages,
  getSortingOrders: sortingOrders,
  getCurrentSortingOrder: currentSortingOrder,
  changeCurrentSortingOrder,
} = useListing({
  listingType: 'categoryListing',
  categoryId: props.category.id,
  defaultSearchCriteria: {
    limit: 24,
    search: '',
    associations: {
      categories: {
        associations: {
          seoUrls: {},
        },
      },
      seoUrls: {},
    },
  },
})

const categories = ref<Schemas['Category'][]>([])
const search = ref('')
const criteria = ref<Schemas['ProductListingCriteria']>({})

const { advancedSearch: searchCategories } = useCategorySearch()

const siblings = computed(() => {
  return (
    categories.value.find(
      (category: Schemas['Category']) => category.id === props.category.parentId
    )?.children ?? categories.value
  )
})
const sortedCategories = () => {
  const children = categories.value as Schemas['Category'][]
  const sorted = []
  let current = children?.find((child) => !child.afterCategoryId)
  while (current) {
    sorted.push(current)
    current = children?.find((child) => child.afterCategoryId === current?.id)
  }
  return sorted
}

if (!props.categoryTree) {
  categories.value = await searchCategories({
    query: {
      associations: {
        seoUrls: {},
      },
      filter: [
        {
          type: 'equals',
          field: 'parentId',
          value: props.category.parentId,
        },
      ],
    },
  })
} else {
  categories.value = props.categoryTree
}
// https://jkwebch.atlassian.net/browse/JPR-284
categories.value = sortedCategories()
const reload = (page?: number) => {
  const searchCriteria: Schemas['Criteria'] = {
    ...criteria.value,
  }
  if (page) {
    searchCriteria.p = page
  }
  if (search.value) {
    searchCriteria.term = search.value
  }
  return searchProducts(searchCriteria)
}

const loadMore = () =>
  coreLoadMore({
    search: '',
    p: page.value + 1,
  })

await reload(route.query.p ? parseInt(route.query.p as string) : undefined)

watchDebounced(
  [criteria, search],
  () => {
    reload()
  },
  { debounce: 500 }
)
</script>
